import { ApplicationState, AppThunkDispatch } from '../../../store';
import { connect } from 'react-redux';
import Home, { HomeStateProps, HomeDispatchProps } from './Home';
import { getEntitiesSearchItems, getEntitiesDataItems } from '../../../store/entities/selectors';
import { EntityType, AppointmentEntity } from '../../../store/entities/state';
import { canEditAppointments, canEditClients, canSeeAppointments, isProfessional } from '../../../store/session/selectors';
import { AppointmentChangeReason, AppointmentData, PaymentStatus, ProfessionalData } from '../../../models';
import { entityCreating, updateEntity, updateChangingEntity, deleteEntity, openAppointment } from '../../../store/entities/actions';
import { deleteAppointmentAsync, updateAppointmentAsync, updateAppointmentConfirmationStatusAsync, updateAppointmentHasHappenedAsync } from '../../../ajax/requests';
import { getSelectedBranch } from '../../../store/general/selectors';
import { getGeneralSettings } from '../../../store/settings/selectors';
import { getAppointmentTotalPayments, getTotalPrice } from '../../../shared/appointmint/appointment';
import { Guid } from '../../../shared/utils';
import { applyAppointmentConfirmationStatus, AppointmentConfirmationEditableStatus } from '../../../shared/appointmint/appointmentConfirmationStatus';

const mapStateToProps = (state: ApplicationState): HomeStateProps => {
    const selectedBranch = getSelectedBranch(state);
    return {
        appointmentsSelected: getEntitiesDataItems<AppointmentEntity>('appointments')(state)
            .filter(a => !!a && (!selectedBranch || a.branchId == selectedBranch)),
        canSeeAppointments: canSeeAppointments(state),
        settings: getGeneralSettings(state),
        professionals: getEntitiesSearchItems<ProfessionalData>(state, 'professionals'),
        canCreateEntities: [
            (isProfessional(state) || canEditAppointments(state)) && 'appointments',
            canEditClients(state) && 'clientPacks',
            canEditClients(state) && 'orders',
        ].filter(x => x) as EntityType[],
    };
}

const mapDispatchToProps = (dispatch: AppThunkDispatch): HomeDispatchProps => ({
    onEditAppointment: (appointment: AppointmentData, forceEdition?: boolean) => {
        dispatch(openAppointment(appointment.id))
        if (forceEdition) dispatch(updateChangingEntity('appointments', appointment))
    },
    onEditPayment: (appointment: AppointmentData) => {
        dispatch(openAppointment(appointment.id, ['edit-payment']))
    },
    onQuickPayment: (appointment: AppointmentData, paymentType: string) => {
        const totalToPay = getTotalPrice(appointment) * (1 + (appointment.vat ?? 0))
        const totalPaid = getAppointmentTotalPayments(appointment)
        const paymentValue = Math.max(0, totalToPay - totalPaid)
        if (paymentValue <= 0) {
            return
        }

        dispatch(updateEntity(
            'appointments',
            {
                ...appointment,
                payments: [
                    ...(appointment.payments ?? []),
                    {
                        id: Guid.newGuid(),
                        date: new Date().toISOString(),
                        type: paymentType,
                        value: paymentValue,
                        status: PaymentStatus.Manual,
                    },
                ],
            },
            a => updateAppointmentAsync(a.id, { data: a }),
        ))
    },
    onCreateInvoice: (appointment: AppointmentData) => {
        dispatch(openAppointment(appointment.id, ['create-invoice']))
    },
    onDeleteAppointment: (appointment: AppointmentData, deleteEntireGroup?: boolean, reason?: AppointmentChangeReason) => dispatch(deleteEntity(
        'appointments',
        appointment.id,
        id => deleteAppointmentAsync(id, deleteEntireGroup ?? false, reason),
        deleteEntireGroup && appointment.recurring?.groupId
            ? (state) => getEntitiesDataItems<AppointmentEntity>('appointments')(state)
                .filter(a => a.recurring?.groupId === appointment.recurring?.groupId && a.date >= appointment.date)
                .map(a => a.id)
            : undefined,
    )),
    onCreateEntity: (entityType: EntityType, initialValue?: any) => dispatch(entityCreating(entityType, initialValue)),
    onMarkAppointmentDone: (appointment: AppointmentData, hasHappened: boolean | undefined) => dispatch(
        updateEntity(
            'appointments',
            {...appointment, hasHappened},
            a => updateAppointmentHasHappenedAsync(a.id, a.hasHappened))),
    onSetAppointmentConfirmationStatus: (appointment: AppointmentData, status: AppointmentConfirmationEditableStatus) => {
        const updated = applyAppointmentConfirmationStatus(appointment, status);
        dispatch(updateEntity(
            'appointments',
            updated,
            a => updateAppointmentConfirmationStatusAsync(a.id, a.confirmedByClientAt, a.deniedByClientAt),
        ));
    },
});

export default connect<HomeStateProps, HomeDispatchProps, void, ApplicationState>(
    mapStateToProps,
    mapDispatchToProps,
)(Home as any);
